import * as chartjs from "chart.js";
import moment, { Moment } from "moment";
import React, { useCallback, useEffect, useRef, useState } from "react";
import { Line } from "react-chartjs-2";
import { Collection } from "../../Model/Collection";
import { DateRange } from "../../Model/Date";
import { ETemperatureSensorType } from "../../Model/ETemperatureSensorType";
import { TemperatureLog, TemperatureSensor } from "../../Model/Temperature";
import { useTypedSelector } from "../../redux/reducers/RootReducer";
import { getModuleTemperatureChanged } from "../../redux/reducers/TemperatureReducer";
import DateUtil from "../../Utility/DateUtil";
import TemperatureApi from "./../../api/TemperatureApi";
import "./temperature.css";
import "./TemperatureChart.css";
import TemperatureChartUtil, {
  TemperatureSensorData,
  TemperatureSetting,
} from "./TemperatureChartUtil";
import TemperatureGraphColor from "./TemperatureGraphColor";
import TemperatureSlider from "./TemperatureSlider";

type Props = {
  moduleIdentifier: string;
  chartDateRange: DateRange;
  setChartDateRange: (dateRange: DateRange) => void;
  temperatureSensorCollection: Collection<TemperatureSensor>;
  style?: React.CSSProperties;
  sensorsVisibility: { [index: string]: boolean };
  temperatureSetting: TemperatureSetting;
  maxXZoom: number;
  isLiveAndMaxSlider: boolean;
  setIsLiveAndMaxSlider: (isLiveAndMaxSlider: boolean) => void;
  firstDataAvailableDate?: Date;
};

const initialChartData: TemperatureSensorData = {
  datasets: [],
};

interface UseRefParams {
  visibleDateMin: Moment;
  visibleDateMax: Moment;
  stepSize: number;
  isScrolling: boolean;
  currentScrollTime: Moment;
  lastUpdate: Moment;
}

export default function TemperatureChart({
  moduleIdentifier,
  temperatureSensorCollection,
  sensorsVisibility,
  chartDateRange,
  setChartDateRange,
  temperatureSetting,
  maxXZoom,
  style = {},
  isLiveAndMaxSlider,
  setIsLiveAndMaxSlider,
  firstDataAvailableDate,
}: Props) {
  const [chartData, setChartData] = useState(initialChartData);
  const [minValues, setMinValues] = useState(
    TemperatureChartUtil.getMinValues(temperatureSensorCollection.allIds)
  );
  const [maxValues, setMaxValues] = useState(
    TemperatureChartUtil.getMaxValues(temperatureSensorCollection.allIds)
  );
  const moduleTemperatureChange = useTypedSelector((state) =>
    getModuleTemperatureChanged(state, moduleIdentifier)
  );
  const [isSliderOnMaxValue, setIsSliderOnMaxValue] = useState(false);
  const stateRef = useRef<UseRefParams>({
    visibleDateMin: moment().subtract(15, "minutes"),
    visibleDateMax: moment().add(15, "minutes"),
    stepSize: TemperatureChartUtil.getZoom(temperatureSetting.xZoom)[1],
    isScrolling: false,
    currentScrollTime: moment(),
    lastUpdate: moment(),
  });

  const refChart = useRef<Line>(null);

  // need add by 1 because the minXZoom is 0
  const handleWidthPercentage = (temperatureSetting.xZoom + 1) / (maxXZoom + 1);

  const updateXZoom = (min: Moment, max: Moment, stepSize: number) => {
    console.log("DEBUG", "TemperatureChart", "X Zoom updated");

    stateRef.current.visibleDateMin = min;
    stateRef.current.visibleDateMax = max;
    stateRef.current.stepSize = stepSize;

    const axes = refChart?.current?.chartInstance?.options?.scales?.xAxes;
    if (axes != null) {
      const axis = axes[0];
      if (axis.ticks != null && axis.time != null) {
        const ticks = axis.ticks;

        ticks.min = stateRef.current.visibleDateMin;
        ticks.max = stateRef.current.visibleDateMax;
        axis.time.unitStepSize = stateRef.current.stepSize;
        refChart?.current?.chartInstance?.update();
      }
    }
  };

  const today = new Date();
  const isLiveChart = DateUtil.isInRange(chartDateRange, today);

  useEffect(() => {
    if (isLiveChart && isSliderOnMaxValue) {
      setIsLiveAndMaxSlider(true);
    } else {
      setIsLiveAndMaxSlider(false);
    }
  }, [chartDateRange, isSliderOnMaxValue]);

  useEffect(() => {
    if (isLiveAndMaxSlider) {
      if (!isLiveChart) {
        setChartDateRange({
          start: moment().startOf("day").toDate(),
          end: moment().endOf("day").toDate(),
        });
      }
      // update the slider to max position
      onSliderUpdated(1440);
      onSliderChanged();
    }
  }, [isLiveAndMaxSlider]);

  //-------Load Temperature Data-------//
  useEffect(() => {
    console.log(
      "DEBUG",
      "TemperatureChart",
      "Temperature date loaded from backend"
    );
    TemperatureApi.getTemperatureLog(moduleIdentifier, chartDateRange).then(
      (temperatureLog: TemperatureLog) => {
        const result = TemperatureChartUtil.getTemperatureSensorData(
          temperatureSensorCollection,
          temperatureLog,
          minValues,
          maxValues,
          sensorsVisibility
        );
        setChartData(result.temperatureSensorData);
        setMinValues(result.minValues);
        setMaxValues(result.maxValues);
      }
    );
  }, [
    moduleIdentifier,
    chartDateRange,
    minValues,
    maxValues,
    temperatureSensorCollection,
    sensorsVisibility,
  ]);

  //-------Update temperature reading value from back end-------//
  useEffect(() => {
    if (!isLiveChart) {
      return;
    }

    const datasets = refChart?.current?.chartInstance?.data?.datasets;
    if (
      datasets != null &&
      moduleTemperatureChange != null &&
      !stateRef.current.isScrolling
    ) {
      //postpone updating chart if still scrolling
      const time = moment(moduleTemperatureChange.time);
      for (let i = 0; i < datasets.length; i++) {
        let data = datasets[i].data as chartjs.ChartPoint[];
        if (data == null) {
          throw new Error("Data is null");
        }
        const sensorName = datasets[i].label || "";
        const newValue = moduleTemperatureChange.data[sensorName];
        if (newValue != null) {
          const point: chartjs.ChartPoint = {
            x: time,
            y: newValue,
          };
          data.push(point);
        }
      }

      //update chartdateEnd based on xZoom
      updateXZoom(
        stateRef.current.visibleDateMin.add(2.5, "seconds"),
        stateRef.current.visibleDateMax.add(2.5, "seconds"),
        stateRef.current.stepSize
      );

      refChart?.current?.chartInstance?.update();
      stateRef.current.lastUpdate = moment();
      console.log("DEBUG", "TemperatureChart", "Updated From Backend");
    }
  }, [moduleIdentifier, moduleTemperatureChange, isLiveChart]);

  // -------Update temperature reading value if there are no updates from back end-------//
  useEffect(() => {
    if (!isLiveChart) {
      return;
    }

    const id = setInterval(() => {
      const time = moment();
      const diff = time.diff(stateRef.current.lastUpdate, "seconds");
      if (
        diff > temperatureSetting.refreshStaleDataInterval &&
        !stateRef.current.isScrolling
      ) {
        console.log("DEBUG", "TemperatureChart", "Updated from front end");
        //postpone updating chart if still scrolling
        const datasets = refChart?.current?.chartInstance?.data?.datasets;
        if (datasets != null) {
          for (let i = 0; i < datasets.length; i++) {
            let data = datasets[i].data as chartjs.ChartPoint[];
            if (data == null) {
              throw new Error("Data is null");
            }
            const index = data.length - 1;
            if (index > 1 && data[index].y === data[index - 1].y) {
              data[index].x = time;
            } else {
              data.push({
                x: time,
                y: data[index].y,
              });
            }
          }
          stateRef.current.lastUpdate = time;

          //update chartdateEnd based on xZoom
          updateXZoom(
            stateRef.current.visibleDateMin.add(2.5, "seconds"),
            stateRef.current.visibleDateMax.add(2.5, "seconds"),
            stateRef.current.stepSize
          );
          
          refChart?.current?.chartInstance?.update();
        }
      }
    }, temperatureSetting.refreshStaleDataInterval * 1000);
    return () => clearInterval(id);
  }, [
    chartDateRange.start,
    chartDateRange.end,
    moduleIdentifier,
    temperatureSetting.refreshStaleDataInterval,
    isLiveChart,
  ]);

  //-------Using refresh interval to reload page-------//
  useEffect(() => {
    console.log("DEBUG", "TemperatureChart", "Reload page", {
      time: moment(),
      refreshInterval: temperatureSetting.refreshInterval,
      isLiveChart: isLiveChart,
    });

    const timeout = setTimeout(() => {
      if (!isLiveChart) {
        return;
      }

      // only reload page when we are viewing live chart
      window.location.reload();
    }, temperatureSetting.refreshInterval * 60000);

    return () => clearTimeout(timeout);
  }, [temperatureSetting.refreshInterval, isLiveChart]);

  //-------Update line visibility based on sensors visibility-------//
  useEffect(() => {
    console.log("DEBUG", "TemperatureChart", "Sensors visibility updated");
    const datasets = refChart?.current?.chartInstance?.data?.datasets;
    if (datasets != null) {
      for (let i = 0; i < datasets.length; i++) {
        const sensorName = temperatureSensorCollection.allIds[i];
        const sensor = temperatureSensorCollection.byId[sensorName];
        const visibility =
          sensor.type === ETemperatureSensorType.Feedback
            ? false
            : sensorsVisibility[sensorName] == null
            ? true
            : sensorsVisibility[sensorName];
        const color = visibility
          ? TemperatureGraphColor.getTemperatureGraphColor(datasets[i].label)
          : "transparent";
        datasets[i].backgroundColor = color;
        datasets[i].borderColor = color;
      }
      refChart?.current?.chartInstance?.update();
    }
  }, [sensorsVisibility, refChart, temperatureSensorCollection.allIds]);

  //-------Update chart option based on X zoom-------//
  useEffect(() => {
    console.log("DEBUG", "TemperatureChart", "X Zoom updated");
    const result = TemperatureChartUtil.getVisibleDatesFromXZoom(
      temperatureSetting.xZoom,
      chartDateRange,
      stateRef.current.currentScrollTime,
      isLiveChart
    );
    updateXZoom(result.min, result.max, result.stepSize);
  }, [temperatureSetting.xZoom, chartDateRange, isLiveChart]);

  //-------Set Best Y Zoom setting-------//
  useEffect(() => {
    console.log("DEBUG", "TemperatureChart", "Best Y Zoom updated");
    if (temperatureSetting.useBestYZoom) {
      const datasets = refChart?.current?.chartInstance?.data?.datasets;
      const axes = refChart?.current?.chartInstance?.options?.scales?.yAxes;
      const axis = axes ? axes[0] : undefined;
      const ticks = axis?.ticks;
      if (axis != null && ticks != null && datasets != null) {
        let { min, max } = TemperatureChartUtil.getMinMaxBestYZoom(
          minValues,
          maxValues,
          datasets,
          temperatureSensorCollection
        );
        ticks.min = min;
        ticks.max = max;
        refChart?.current?.chartInstance?.update();
      }
    }
  }, [
    temperatureSetting.useBestYZoom,
    refChart,
    minValues,
    maxValues,
    temperatureSensorCollection.byId,
  ]);

  //-------Set Best Y Zoom min max setting-------//
  useEffect(() => {
    console.log("DEBUG", "TemperatureChart", "Y Zoom min max updated");
    if (!temperatureSetting.useBestYZoom) {
      const { minTemp, maxTemp, minOutput, maxOutput } = temperatureSetting;
      const yAxes = refChart?.current?.chartInstance?.options?.scales?.yAxes;
      const temperatureAxis = yAxes ? yAxes[0] : undefined;
      const outputAxis = yAxes ? yAxes[1] : undefined;
      if (
        temperatureAxis != null &&
        outputAxis != null &&
        temperatureAxis.ticks != null &&
        outputAxis.ticks != null
      ) {
        temperatureAxis.ticks.min = minTemp;
        temperatureAxis.ticks.max = maxTemp;
        outputAxis.ticks.min = minOutput;
        outputAxis.ticks.max = maxOutput;
        refChart?.current?.chartInstance?.update();
      }
    }
  }, [refChart, temperatureSetting]);

  //-------Set show tooltip-------//
  useEffect(() => {
    console.log("DEBUG", "TemperatureChart", "Show Tooltip updated");
    const tooltips = refChart?.current?.chartInstance?.options?.tooltips;
    if (tooltips != null) {
      tooltips.enabled = temperatureSetting.showTooltip;
      refChart?.current?.chartInstance?.update();
    }
  }, [refChart, temperatureSetting.showTooltip]);

  const onSliderChanged = useCallback(() => {
    stateRef.current.isScrolling = false;
  }, []);

  const onSliderUpdated = useCallback(
    (value: number) => {
      console.log("DEBUG", "TemperatureChart", "Slider Updated");
      stateRef.current.isScrolling = true;
      const [timeWindow] = TemperatureChartUtil.getZoom(
        temperatureSetting.xZoom
      );
      //Y = C + X * (D - C) / (B - A)
      const a = 0; //min slider
      const b = 1440; //max slider
      const c = moment(chartDateRange.start)
        .startOf("day")
        .add(timeWindow, "minutes"); //min time
      let d = moment(chartDateRange.end).endOf("day"); //max time
      if (isLiveChart) {
        d = moment().add(
          TemperatureChartUtil.getLiveIntervalMinutesXZoom(
            temperatureSetting.xZoom
          ),
          "minutes"
        );
      }
      const y = moment(c).add(
        value * (d.diff(c, "minutes") / (b - a)),
        "minutes"
      );
      const max = y;
      const min = moment(y).subtract(timeWindow, "minutes");
      stateRef.current.currentScrollTime = moment(y).subtract(
        timeWindow / 2,
        "minutes"
      );
      updateXZoom(min, max, stateRef.current.stepSize);

      if (value >= b) {
        setIsSliderOnMaxValue(true);
      } else {
        setIsSliderOnMaxValue(false);
      }
    },
    [chartDateRange, temperatureSetting.xZoom, isLiveChart]
  );

  const onButtonYesterdayClicked = () => {
    const currentStartDate = moment(chartDateRange.start);
    const currentEndDate = moment(chartDateRange.end);
    const previousStartDate = currentStartDate.subtract(1, "days");
    const previousEndDate = currentEndDate.subtract(1, "days");

    setChartDateRange({
      start: previousStartDate.toDate(),
      end: previousEndDate.toDate(),
    });
  };

  const onButtonTomorrowClicked = () => {
    const currentStartDate = moment(chartDateRange.start);
    const currentEndDate = moment(chartDateRange.end);
    const previousStartDate = currentStartDate.add(1, "days");
    const previousEndDate = currentEndDate.add(1, "days");

    setChartDateRange({
      start: previousStartDate.toDate(),
      end: previousEndDate.toDate(),
    });
  };

  //HACK: use negative number when not want to update TemperatureSlider
  const getInitialCurrentScrollbarPosition = () => {
    if (isLiveChart && isLiveAndMaxSlider) {
       return 1440;
    } else {
      return -1;
    }
  };

  console.log("DEBUG", "TemperatureChart", "Rendered");

  return (
    <div
      className="temperature-chart-container"
      style={{
        ...style,
      }}
    >
      {/* -------Temperature graph------- */}
      <div className="temperature-chart">
        <div
          style={{
            position: "relative",
            width: "100%",
            height: "100%",
            overflowX: "hidden",
            overflowY: "hidden",
          }}
        >
          <div
            style={{
              position: "absolute",
              width: "100%",
              height: "100%",
              overflowX: "hidden",
              overflowY: "hidden",
            }}
          >
            <Line
              ref={refChart}
              data={chartData}
              options={TemperatureChartUtil.getInitialChartOption(
                stateRef.current.visibleDateMin,
                stateRef.current.visibleDateMax,
                temperatureSetting,
                stateRef.current.stepSize,
                chartData.datasets,
                minValues,
                maxValues,
                temperatureSensorCollection
              )}
              width={100}
              height={100}
            />
          </div>
        </div>
      </div>

      <span className="temperature-label">Time</span>
      <span className="temperature-date">
        {DateUtil.toString(chartDateRange.start)} to{" "}
        {DateUtil.toString(chartDateRange.end)}
      </span>

      {/* -------Temperature graph scrollbar------- */}
      <div className="temperature-slider">
        <TemperatureSlider
          onChanged={onSliderChanged}
          onUpdated={onSliderUpdated}
          onButtonYesterdayClicked={onButtonYesterdayClicked}
          onButtonTomorrowClicked={onButtonTomorrowClicked}
          handleWidthPercentage={handleWidthPercentage}
          min={0}
          max={1440}
          value={getInitialCurrentScrollbarPosition()}
          disableTomorrow={chartDateRange.end >= new Date()}
          disableYesterday={DateUtil.isSameDay(
            chartDateRange.start,
            firstDataAvailableDate
              ? firstDataAvailableDate
              : new Date("0001-01-01T00:00:00Z")
          )}
        />
      </div>
    </div>
  );
}

function temperatureChartPropsAreEqual(prev: Props, next: Props) {
  if (prev.moduleIdentifier !== next.moduleIdentifier) {
    return false;
  }

  if (prev.chartDateRange.start !== next.chartDateRange.start) {
    return false;
  }

  if (prev.chartDateRange.end !== next.chartDateRange.end) {
    return false;
  }

  const keys = Object.keys(prev.sensorsVisibility);

  for (let i = 0; i < keys.length; i++) {
    const key = keys[i];
    if (prev.sensorsVisibility[key] !== next.sensorsVisibility[key]) {
      return false;
    }
  }

  if (prev.setChartDateRange !== next.setChartDateRange) {
    return false;
  }

  if (prev.temperatureSetting !== next.temperatureSetting) {
    return false;
  }

  if (prev.isLiveAndMaxSlider !== next.isLiveAndMaxSlider) {
    return false;
  }

  return true;
}

export const MemoizedTemperatureChart = React.memo(
  TemperatureChart,
  temperatureChartPropsAreEqual
);

TemperatureChart.whyDidYouRender = true;
